import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import { SUPPORTED_LANGUAGES } from "@/i18n/translations";
import AccountShell from "@/components/vangard/AccountShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREMIUM_TIERS = new Set(["gold", "vip", "corporate"]);

function Row({ label, children, testid }) {
  return (
    <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={testid}>
      <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500 sm:w-56 shrink-0">{label}</div>
      <div className="flex-1 sm:text-right min-w-0 text-sm">{children}</div>
    </div>
  );
}

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} disabled={disabled}
      onClick={() => onChange(!on)} data-testid={testid}
      className={`inline-flex items-center py-2 ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <span className={`relative flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-white" : "bg-white/[0.12]"}`}>
        <span className={`inline-block h-4 w-4 rounded-full transition-transform ${on ? "bg-black translate-x-6" : "bg-white translate-x-1"}`} />
      </span>
    </button>
  );
}

export default function AccountSettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const { t, lang, setLang } = useTranslation();
  const navigate = useNavigate();
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => { setIsPublic(!!user?.is_public); }, [user?.is_public]);

  if (!user) return <AccountShell active="/account/settings"><div /></AccountShell>;

  const tier = (user.membership_tier || "silver").toLowerCase();
  const isPremium = PREMIUM_TIERS.has(tier);
  const status = user.application_status || "none";

  const toggleVisibility = async (next) => {
    setIsPublic(next);
    try {
      await axios.patch(`${API}/auth/me`, { is_public: next }, { withCredentials: true });
      await refreshUser();
      toast.success(next ? t("vc.acct.now-public") : t("vc.acct.now-private"));
    } catch {
      setIsPublic(!next);
      toast.error(t("vc.account.save-fail"));
    }
  };

  return (
    <AccountShell active="/account/settings" title={t("vc.acct.nav.settings")}>
      <div data-testid="account-settings-page">
        <p className="text-sm text-neutral-400 font-light max-w-lg">{t("vc.acct.set.sub")}</p>

        <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 divide-y divide-white/[0.06]">
          <Row label={t("vc.account.full-name")} testid="setting-name">
            <span className="text-base font-light">{user.name}</span>
          </Row>
          <Row label="Email" testid="setting-email">
            <span className="text-base font-light break-all">{user.email}</span>
          </Row>
          <Row label={t("vc.acct.member-id")} testid="setting-member-id">
            <span className="font-mono text-base">#{user.member_id}</span>
          </Row>
          <Row label={t("vc.acct.tier")} testid="setting-tier">
            <div className="flex sm:justify-end items-center gap-4">
              <span className="text-[11px] tracking-[0.25em] uppercase">{tier}</span>
              <Link
                to="/account/membership"
                className="inline-flex items-center border border-white/20 px-5 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                data-testid="setting-upgrade"
              >
                {t("vc.acct.set.upgrade")}
              </Link>
            </div>
          </Row>
          <Row label={t("vc.acct.app-status")} testid="setting-app-status">
            <div className="flex sm:justify-end items-center gap-4">
              <span className={`text-[11px] tracking-[0.25em] uppercase ${status === "approved" ? "" : "text-[#E5484D]"}`}>{t(`vc.onb.status.${status}`)}</span>
              <Link
                to="/onboarding"
                className="inline-flex items-center border border-white/20 px-5 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                data-testid="setting-open-application"
              >
                {t("vc.acct.set.application")}
              </Link>
            </div>
          </Row>
          <Row label={t("vc.account.visibility")} testid="setting-visibility">
            <div className="flex sm:justify-end">
              <Toggle on={isPublic && isPremium} onChange={toggleVisibility} disabled={!isPremium} testid="settings-visibility-toggle" />
            </div>
          </Row>
          <Row label={t("vc.acct.set.language")} testid="setting-language">
            <div className="flex sm:justify-end gap-2">
              {SUPPORTED_LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`setting-lang-${l.code}`}
                  className={`px-4 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase transition-colors ${
                    lang === l.code ? "bg-white text-black" : "border border-white/15 text-neutral-400 hover:text-white hover:border-white/40"
                  }`}
                >
                  {l.short}
                </button>
              ))}
            </div>
          </Row>
          <Row label={t("vc.acct.nav.profile")} testid="setting-profile">
            <div className="flex sm:justify-end">
              <Link
                to="/account/profile"
                className="inline-flex items-center border border-white/20 px-5 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                data-testid="setting-edit-profile"
              >
                {t("vc.account.edit-profile-btn")}
              </Link>
            </div>
          </Row>
        </div>

        <button
          onClick={async () => { await logout(); navigate("/"); }}
          className="mt-6 inline-flex items-center gap-2 border border-white/15 text-neutral-300 px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:text-white hover:border-white/40 transition-colors"
          data-testid="settings-logout"
        >
          <LogOut size={13} /> {t("menu.logout")}
        </button>
      </div>
    </AccountShell>
  );
}
